// apps/main/src/clark-widget/ModalOrb.jsx — RUN AB. The capacity modal's orb.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE ORB IS REAL, NOT DECORATIVE, AND IT IS NOT A SECOND IMPLEMENTATION.
//
// This file contains no orb MATH. The six metaballs, the theme-hue rotation, the eased speed ramp
// and the RAF loop all live in client/src/components/orbController.js and are reached through
// `registerOrb` — the same call navOrb.js and homeOrb.js make. Re-implementing drawOrb here is
// exactly how apps/seafoam ended up with a second copy that had to be kept in sync by hand.
//
// ── 🔴 THE BUNDLE PROBLEM, WHICH IS THE REAL REASON THIS FILE NEEDED THINKING ABOUT ────────────
//
// orbController.js is a module singleton — but PER BUNDLE, not per page. homeOrb.js's header spells
// out the consequence: an island with its own import of orbController gets "a SECOND copy … so the
// page would run TWO clocks: the nav orb and the widget orb would drift apart on screen and
// pulseOrb() from one would never reach the other."
//
// This modal lives in clark-widget.bundled.js. The homepage orb behind the scrim lives in
// header.bundled.js. Those are two bundles, so importing orbController here would have produced
// exactly the documented failure — the modal's orb would speed up alone, and the blurred orb behind
// it would carry on at its own pace. That is the §7 requirement inverted.
//
// The fix is in orbController.js itself (RUN AB's bridge block): the first copy to load publishes
// its clock on `window.__ccOrbClock`, and every later copy ADOPTS it — registerOrb, setOrbTyping
// and pulseOrb all delegate. header.bundled.js loads before clark-widget.bundled.js on home.html
// (assemble.mjs appends the widget script at </body>, after the header island), so in practice the
// header island publishes and this bundle adopts. It is order-independent either way: whichever
// loads first owns the clock, and there is exactly one per page.
//
// SO: this canvas registers on the SAME clock as the homepage orb, draws from the SAME phase, and
// clicking it calls the SAME pulseOrb — which is why every visible orb responds rather than just
// this one. Nothing here rebuilds the movement animation or the click-to-speed-up; both already
// existed and both are reached, not reimplemented.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";
import { registerOrb, pulseOrb } from "../../client/src/components/orbController.js";

export default function ModalOrb({ size = 72, className }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    // 2× backing store, the same retina convention navOrb.js, homeOrb.js and ClarkOrb.jsx use.
    const px = size * 2;
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    // Registering is what puts this canvas on the shared clock; the returned fn unregisters it, and
    // orbController stops the RAF when the last orb goes — so dismissing the modal leaks no frames.
    return registerOrb({ ctx, px });
  }, [size]);

  return (
    <button
      type="button"
      // 🔴 >=44px tap target (§7). The canvas is `size` px; the button pads out to at least 44.
      style={{
        width: Math.max(44, size), height: Math.max(44, size),
        padding: 0, border: 0, background: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%",
      }}
      // pulseOrb() touches the SHARED clock rather than this canvas, so this click speeds up every
      // registered orb on the page — including the homepage one blurred behind the scrim.
      onClick={() => pulseOrb()}
      aria-label="Clark"
      data-testid="clark-capacity-orb"
      className={className}
    >
      <canvas ref={ref} style={{ width: size, height: size, borderRadius: "50%" }} aria-hidden="true" />
    </button>
  );
}
